import { createI18n } from 'vue-i18n';
import axios from 'axios';
import en from './locales/en';
import es from './locales/es';
import zh from './locales/zh';

export const SUPPORTED_LOCALES = ['en', 'es', 'zh'] as const;
export type AppLocale = typeof SUPPORTED_LOCALES[number];

export const LOCALE_STORAGE_KEY = 'dream_locale';

export function isAppLocale(value: string | null | undefined): value is AppLocale {
    return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function detectBrowserLocale(): AppLocale {
    if (typeof navigator === 'undefined') return 'en';
    const candidates = [
        ...(navigator.languages || []),
        navigator.language,
    ].filter(Boolean) as string[];

    for (const raw of candidates) {
        const lower = raw.toLowerCase();
        if (lower.startsWith('zh')) return 'zh';
        if (lower.startsWith('es')) return 'es';
        if (lower.startsWith('en')) return 'en';
    }
    return 'en';
}

export function resolveInitialLocale(): AppLocale {
    try {
        const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
        if (isAppLocale(saved)) return saved;
    } catch {
        // ignore
    }
    return detectBrowserLocale();
}

export function applyDocumentLocale(locale: AppLocale) {
    if (typeof document !== 'undefined') {
        document.documentElement.lang = locale === 'zh' ? 'zh-CN' : locale;
    }
}

const initialLocale = resolveInitialLocale();
applyDocumentLocale(initialLocale);

const i18n = createI18n({
    legacy: false,
    locale: initialLocale,
    fallbackLocale: 'en',
    globalInjection: true,
    messages: { en, es, zh },
});

export function setAppLocale(locale: AppLocale) {
    if (!isAppLocale(locale)) return;
    i18n.global.locale.value = locale;
    applyDocumentLocale(locale);
    try {
        localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
        // ignore
    }
    axios.defaults.headers.common['X-Locale'] = locale;
    axios.defaults.headers.common['Accept-Language'] = locale;
}

export default i18n;
